/**
 * ProBookingConfig.jsx
 *
 * Account-level configuration for the Pro public booking page.
 * Route: /pro/booking-config  (ACCOUNT_OWNER only)
 *
 * Covers:
 *   - Booking page live toggle + slug + business name/bio
 *   - Salon opening hours (ceiling for all stylist availability)
 *   - Deposit type / value
 *   - Buffer / lead time / advance booking window
 *   - Stripe Connect onboarding
 *
 * Stylist toggles and service matrix live in Probookingsettings.jsx.
 *
 * API: GET/PUT /api/pro-booking/account-config
 *      POST    /api/pro-booking/stripe-connect  (to be added — mirrors Solo)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Switch, FormControlLabel,
  MenuItem, Button, Alert, Divider, CircularProgress,
  InputAdornment, Grid, Chip, IconButton,
} from '@mui/material';
import OpenInNewIcon    from '@mui/icons-material/OpenInNew';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import SaveIcon         from '@mui/icons-material/Save';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
};

const DAYS = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

const DEFAULT_SALON_HOURS = {
  mon: ['09:00', '17:00'],
  tue: ['09:00', '17:00'],
  wed: ['09:00', '17:00'],
  thu: ['09:00', '17:00'],
  fri: ['09:00', '17:00'],
  sat: [],
  sun: [],
};

const PUBLIC_BASE = `${typeof window !== 'undefined' ? window.location.origin : ''}/book/`;

export default function ProBookingConfig() {
  const navigate = useNavigate();
  const [config,   setConfig]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/pro-booking/account-config');
      // If salon_hours is null (never configured), seed with defaults
      if (!data.salon_hours) {
        data.salon_hours = DEFAULT_SALON_HOURS;
      }
      setConfig(data);
    } catch (e) {
      setError('Could not load booking configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // If returning from Stripe onboarding, refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'return') {
      window.history.replaceState({}, '', '/pro/booking-config');
      load();
    }
  }, [load]);

  const update = (patch) => setConfig((c) => ({ ...c, ...patch }));

  const updateHours = (day, idx, value) => {
    setConfig((c) => {
      const sh  = { ...(c.salon_hours || {}) };
      const arr = [...(sh[day] || ['09:00', '17:00'])];
      if (arr.length < 2) arr.push('', '');
      arr[idx] = value;
      sh[day]  = arr.slice(0, 2);
      return { ...c, salon_hours: sh };
    });
  };

  const toggleDay = (day, closed) => {
    setConfig((c) => {
      const sh = { ...(c.salon_hours || {}) };
      sh[day]  = closed ? [] : ['09:00', '17:00'];
      return { ...c, salon_hours: sh };
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await apiFetch('/api/pro-booking/account-config', {
        method: 'PUT',
        body: JSON.stringify({
          business_name:            config.business_name,
          salon_bio:                config.salon_bio,
          booking_slug:             config.booking_slug,
          online_booking_enabled:   config.online_booking_enabled,
          deposit_type:             config.deposit_type,
          deposit_value:            Number(config.deposit_value || 0),
          booking_lead_time_hours:  Number(config.booking_lead_time_hours || 2),
          advance_booking_days:     Number(config.advance_booking_days || 60),
          buffer_minutes:           Number(config.buffer_minutes || 0),
          salon_hours:              config.salon_hours,
        }),
      });
      setSuccess('Configuration saved');
      setTimeout(() => setSuccess(''), 3000);
      // ADDED 10 July 2026: refetch so a just-saved booking_slug immediately
      // shows as locked (disabled field + updated helper text) without
      // requiring a page reload — the backend now rejects any further
      // change to it, so the UI should reflect that state right away.
      await load();
    } catch (e) {
      setError(e.message || 'Could not save configuration');
    } finally {
      setSaving(false);
    }
  };

  const connectStripe = async () => {
    try {
      const res = await apiFetch('/api/pro-booking/stripe-connect', { method: 'POST' });
      window.location.href = res.onboarding_url;
    } catch (e) {
      setError(e.message || 'Could not start Stripe onboarding');
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await apiFetch('/api/pro-booking/stripe-login');
      window.open(res.url, '_blank');
    } catch (e) {
      setError('Could not open Stripe dashboard');
    }
  };

  if (loading || !config) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const canEnable = config.stripe_onboarded || config.deposit_type === 'none';
  const bookingUrl = `${PUBLIC_BASE}${config.booking_slug}`;

  return (
   <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
    <IconButton size="small" onClick={() => navigate('/pro/booking-settings')}>
      <ArrowBackIcon />
    </IconButton>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>
      Online Booking — Configuration
    </Typography>
  </Box>
  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
    Configure your public booking page, salon hours, and payment settings.
    Manage which stylists and services are bookable in{' '}
    <strong>Booking Settings</strong>.
  </Typography>

      {/* ── Live toggle ── */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!config.online_booking_enabled}
              disabled={!canEnable}
              onChange={(e) => update({ online_booking_enabled: e.target.checked })}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Booking page live
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {config.online_booking_enabled
                  ? `Live at ${bookingUrl}`
                  : 'Your booking page is currently offline'}
              </Typography>
            </Box>
          }
        />
        {!canEnable && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Connect Stripe below before enabling bookings with a deposit.
          </Alert>
        )}
        {config.online_booking_enabled && config.booking_slug && (
          <Button
            sx={{ mt: 2 }}
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            href={bookingUrl}
            target="_blank"
          >
            View booking page
          </Button>
        )}
      </Paper>

      {/* ── Stripe Connect ── */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Payments
        </Typography>
        {config.stripe_onboarded ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography>Stripe account connected</Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={openStripeDashboard}
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              Open Stripe dashboard
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect a Stripe account to accept deposits. Payments go directly
              to your salon — SalonLogic never holds the money.
            </Typography>
            <Button variant="contained" onClick={connectStripe}>
              {config.stripe_account_id
                ? 'Continue Stripe setup'
                : 'Connect Stripe account'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Page details ── */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Page details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Salon / business name"
              value={config.business_name}
              onChange={(e) => update({ business_name: e.target.value })}
              helperText="Shown to clients on your booking page"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Booking page URL"
              value={config.booking_slug}
              disabled={!!config.booking_slug}
              onChange={(e) =>
                update({
                  booking_slug: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]+/g, '-')
                    .replace(/^-+|-+$/g, ''),
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{PUBLIC_BASE}</InputAdornment>
                ),
              }}
              helperText={
                config.booking_slug
                  ? 'Your booking page URL is locked once set — contact support to change it.'
                  : 'Choose carefully — this cannot be changed once saved.'
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Salon bio (optional)"
              value={config.salon_bio || ''}
              onChange={(e) => update({ salon_bio: e.target.value })}
              helperText="Shown below your salon name on the booking page"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Salon opening hours ── */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Salon opening hours
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sets the ceiling for online bookings. Clients will only see slots
          within these hours, even if a stylist's rota extends beyond them.
          Mark a day closed to prevent any bookings that day regardless of
          individual rotas.
        </Typography>

        {DAYS.map(([key, label]) => {
          const hours  = config.salon_hours?.[key] || [];
          const closed = hours.length !== 2;
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ width: 100, fontWeight: 500 }}>{label}</Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!closed}
                    onChange={(e) => toggleDay(key, !e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {closed ? 'Closed' : 'Open'}
                  </Typography>
                }
                sx={{ minWidth: 110 }}
              />
              {!closed && (
                <>
                  <TextField
                    type="time"
                    size="small"
                    value={hours[0] || ''}
                    onChange={(e) => updateHours(key, 0, e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <Typography variant="body2">to</Typography>
                  <TextField
                    type="time"
                    size="small"
                    value={hours[1] || ''}
                    onChange={(e) => updateHours(key, 1, e.target.value)}
                    sx={{ width: 130 }}
                  />
                </>
              )}
            </Box>
          );
        })}
      </Paper>

      {/* ── Deposit & policies ── */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Deposit & booking rules
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select fullWidth
              label="Deposit type"
              value={config.deposit_type || 'none'}
              onChange={(e) => update({ deposit_type: e.target.value })}
            >
              <MenuItem value="none">No deposit</MenuItem>
              <MenuItem value="fixed">Fixed amount (£)</MenuItem>
              <MenuItem value="percent">Percentage of service cost</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label={config.deposit_type === 'percent' ? 'Percentage' : 'Amount'}
              value={config.deposit_value || 0}
              onChange={(e) => update({ deposit_value: e.target.value })}
              disabled={!config.deposit_type || config.deposit_type === 'none'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {config.deposit_type === 'percent' ? '%' : '£'}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth type="number"
              label="Lead time"
              value={config.booking_lead_time_hours || 2}
              onChange={(e) => update({ booking_lead_time_hours: e.target.value })}
              helperText="Minimum hours before an appointment that clients can book"
              InputProps={{
                endAdornment: <InputAdornment position="end">hours</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth type="number"
              label="Advance booking window"
              value={config.advance_booking_days || 60}
              onChange={(e) => update({ advance_booking_days: e.target.value })}
              helperText="How far ahead clients can book"
              InputProps={{
                endAdornment: <InputAdornment position="end">days ahead</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth type="number"
              label="Buffer between appointments"
              value={config.buffer_minutes || 0}
              onChange={(e) => update({ buffer_minutes: e.target.value })}
              helperText="Gap after each booking before the next slot appears"
              InputProps={{
                endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Save ── */}
      <Box sx={{ display: 'flex', gap: 2, position: 'sticky', bottom: 16 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={save}
          disabled={saving}
          sx={{ boxShadow: 3 }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  );
}